// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Button, Group, Modal, Paper, Select, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { getReferenceString } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import * as Sentry from '@sentry/react';
import { IconChevronDown, IconChevronUp, IconEdit, IconLock, IconPlus } from '@tabler/icons-react';
import type { JSX } from 'react';
import { Fragment, useState } from 'react';
import { type Treatment, treatmentFields, usePatientTreatments } from '../../hooks/usePatientTreatments';
import { useRole } from '../../hooks/useRole';
import { showErrorNotification } from '../../utils/notifications';
import type { PatientTreatment } from '../../utils/patient-treatments';
import { hasPermission, type Permission } from '../../utils/permissions';
import { saveTreatmentCarePlan } from '../../utils/treatment-care-plans';

const MODALITIES = ['Behavioral Therapy', 'Positional Pillow', 'Weight Loss', 'Oral appliance', 'Other'];
const TODAY = new Date();

interface Props {
  patient: Patient;
  place: 'profile' | 'note';
  locked?: boolean;
}

function displayModality(treatment: Treatment): string {
  return treatment.modality === 'Other' && 'other' in treatment && treatment.other
    ? `Other · ${treatment.other}`
    : treatment.modality;
}

function dateText(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${value}T00:00:00Z`)
  );
}

export function PatientTreatmentsSection({ patient, place, locked = false }: Props): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role, isAdmin } = useRole();
  const { treatments, loading, refresh } = usePatientTreatments(patient);
  const permission: Permission = place === 'profile' ? 'manage-patient-treatments' : 'manage-encounter-treatments';
  const canWrite = !locked && hasPermission({ role, isAdmin, permission });
  const [modal, setModal] = useState<'add' | 'edit' | 'end' | undefined>();
  const [selectedId, setSelectedId] = useState<string>();
  const [expandedId, setExpandedId] = useState<string>();
  const [modality, setModality] = useState('');
  const [other, setOther] = useState('');
  const [start, setStart] = useState<string | null>(null);
  const [end, setEnd] = useState<string | null>(null);
  const selected = treatments.find((treatment) => treatment.id === selectedId);
  const profileReference = profile ? getReferenceString(profile) : undefined;

  const openAdd = (): void => {
    setSelectedId(undefined);
    setModality('');
    setOther('');
    setStart(null);
    setEnd(null);
    setModal('add');
  };

  const openEdit = (treatment: Treatment): void => {
    setSelectedId(treatment.id);
    setModality(treatment.modality);
    setOther('other' in treatment ? (treatment.other ?? '') : '');
    setStart(treatment.start);
    setEnd(treatment.end ?? null);
    setModal('edit');
  };

  const save = async (): Promise<void> => {
    if (!start || !modality || (modality === 'Other' && !other.trim())) {
      return;
    }
    const next: Omit<PatientTreatment, 'id'> = {
      ...(selected ? treatmentFields(selected) : { source: 'manual' as const, addedBy: profileReference }),
      modality,
      start,
      other: modality === 'Other' ? other.trim() : undefined,
    };
    try {
      await saveTreatmentCarePlan(medplum, patient.id ?? '', next, {
        existing: selected?.carePlan,
        legacyKey: selected?.legacyKey,
      });
      setModal(undefined);
      await refresh();
    } catch (error) {
      Sentry.captureException(error);
      showErrorNotification(error);
    }
  };

  const saveEnd = async (): Promise<void> => {
    if (!selected || !end) {
      return;
    }
    try {
      await saveTreatmentCarePlan(
        medplum,
        patient.id ?? '',
        { ...treatmentFields(selected), end, endedBy: profileReference },
        { existing: selected.carePlan, legacyKey: selected.legacyKey }
      );
      setModal(undefined);
      await refresh();
    } catch (error) {
      Sentry.captureException(error);
      showErrorNotification(error);
    }
  };

  const title = place === 'note' ? 'Treatment Initiation' : 'Treatments';
  let modalTitle = 'Add treatment';
  if (modal === 'edit') {
    modalTitle = 'Edit treatment';
  } else if (modal === 'end') {
    modalTitle = 'End treatment';
  }
  const profileName = profile?.name?.[0]
    ? `${profile.name[0].given?.join(' ') ?? ''}${profile.name[0].family ? `, ${profile.name[0].family}` : ''}`.trim()
    : undefined;
  let tableContent: JSX.Element;
  if (loading) {
    tableContent = <Text c="dimmed">Loading treatments…</Text>;
  } else if (treatments.length === 0) {
    tableContent = (
      <Box
        py="xl"
        px="md"
        style={{
          border: '1px dashed var(--mantine-color-gray-4)',
          borderRadius: 10,
          background: 'var(--mantine-color-gray-0)',
          textAlign: 'center',
        }}
      >
        <Text fw={600} c="dark">
          Treatments are not yet tracked for this patient.
        </Text>
        <Text size="sm" c="dimmed" maw={520} mx="auto" mt={6}>
          Add a treatment episode to record when care began. DME rows appear automatically once a device prescription is
          filed.
        </Text>
        {canWrite && (
          <Button variant="light" mt="md" onClick={openAdd}>
            Add treatment
          </Button>
        )}
      </Box>
    );
  } else {
    tableContent = (
      <Table highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Modality</Table.Th>
            <Table.Th>Start date</Table.Th>
            <Table.Th>End date</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {treatments
            .slice()
            .sort((a, b) => b.start.localeCompare(a.start))
            .map((treatment) => {
              const expanded = expandedId === treatment.id;
              return (
                <Fragment key={treatment.id}>
                  <Table.Tr key={treatment.id}>
                    <Table.Td>
                      <Button
                        variant="subtle"
                        size="compact-sm"
                        onClick={() => setExpandedId(expanded ? undefined : treatment.id)}
                        leftSection={expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                      >
                        <>
                          {'dme' in treatment && <IconLock size={13} />} {displayModality(treatment)}
                        </>
                      </Button>
                    </Table.Td>
                    <Table.Td>{dateText(treatment.start)}</Table.Td>
                    <Table.Td>{dateText(treatment.end)}</Table.Td>
                    <Table.Td>{treatment.end ? 'Completed' : 'Active'}</Table.Td>
                    <Table.Td>
                      {canWrite && (
                        <Group gap="xs">
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            leftSection={<IconEdit size={13} />}
                            onClick={() => openEdit(treatment)}
                          >
                            Edit
                          </Button>
                          {!treatment.end && (
                            <Button
                              variant="subtle"
                              color="red"
                              size="compact-xs"
                              onClick={() => {
                                setSelectedId(treatment.id);
                                setEnd(null);
                                setModal('end');
                              }}
                            >
                              End treatment
                            </Button>
                          )}
                        </Group>
                      )}
                    </Table.Td>
                  </Table.Tr>
                  {expanded && (
                    <Table.Tr key={`${treatment.id}-detail`}>
                      <Table.Td colSpan={5}>
                        <Text size="sm">
                          <b>{'dme' in treatment ? 'Source' : 'Added by'}</b>:{' '}
                          {'dme' in treatment
                            ? treatment.orderName
                            : (treatment.addedByName ?? treatment.addedBy ?? 'Unknown')}
                        </Text>
                        {'dme' in treatment && (
                          <Text size="xs" c="dimmed">
                            DME modality comes from device prescription and is read-only here.
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Fragment>
              );
            })}
        </Table.Tbody>
      </Table>
    );
  }
  let permissionText = 'Physicians, NPs, and Care Coordinators can add, edit, and end treatments here.';
  if (!canWrite) {
    permissionText =
      'View only. Physician or NP can manage treatments in an encounter note; Care Coordinators can manage them from the patient profile.';
  } else if (place === 'note') {
    permissionText =
      'Treatment changes write directly to the patient record and remain editable after this note is signed.';
  }
  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={place === 'note' ? 3 : 2}>
            {title}
            {treatments.length > 0 ? ` (${treatments.length})` : ''}
          </Title>
          {canWrite && treatments.length > 0 && (
            <Button size="sm" leftSection={<IconPlus size={15} />} onClick={openAdd}>
              Add treatment
            </Button>
          )}
        </Group>
        <Text size="sm" c="dimmed">
          {place === 'note'
            ? 'Record when this patient treatment began without leaving the note.'
            : 'Every course of treatment this patient has been on, most recent first.'}
        </Text>
        <Box
          px="sm"
          py={8}
          style={{
            border: '1px solid var(--mantine-color-gray-3)',
            borderRadius: 8,
            background: !canWrite ? 'var(--mantine-color-yellow-0)' : 'var(--mantine-color-gray-0)',
          }}
        >
          <Text size="xs" c={!canWrite ? 'orange' : 'dimmed'}>
            {permissionText}
          </Text>
        </Box>
        {tableContent}
        <Modal
          opened={Boolean(modal)}
          onClose={() => setModal(undefined)}
          title={
            <Stack gap={0}>
              <Title order={2} c="#3a0929">
                {modalTitle}
              </Title>
              <Text size="sm" c="dimmed">
                {modal === 'end'
                  ? 'Ends the treatment on the patient record.'
                  : `Writes to the patient record${profileName ? ` · ${profileName}` : ''}`}
              </Text>
            </Stack>
          }
          size={520}
          radius="lg"
          yOffset={10}
          styles={{
            header: { padding: '22px 24px 0', alignItems: 'flex-start' },
            title: { width: '100%' },
            body: { padding: '18px 24px 22px' },
            close: { color: 'var(--mantine-color-gray-6)' },
          }}
        >
          {modal === 'end' ? (
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                {selected && displayModality(selected)} will move to Completed.
              </Text>
              <DateInput
                label="End date"
                size="md"
                value={end ? new Date(`${end}T00:00:00`) : null}
                onChange={(value) => setEnd(value)}
                minDate={new Date(`${selected?.start}T00:00:00`)}
                maxDate={TODAY}
                required
              />
              <Group justify="flex-end" mt="xs">
                <Button variant="default" onClick={() => setModal(undefined)}>
                  Cancel
                </Button>
                <Button color="red" onClick={() => saveEnd().catch(() => undefined)}>
                  End treatment
                </Button>
              </Group>
            </Stack>
          ) : (
            <Stack gap="md">
              <Select
                label="Modality"
                data={MODALITIES}
                placeholder="Select a modality"
                size="md"
                value={modality}
                onChange={(value) => setModality(value ?? '')}
                required
                disabled={Boolean(selected && 'dme' in selected)}
              />
              <Text size="sm" c="dimmed">
                DME isn&apos;t an option here — a DME treatment is created automatically from the device prescription in
                the note&apos;s Orders section. Everything else is added by hand.
              </Text>
              {modality === 'Other' && (
                <TextInput
                  label="Describe the treatment"
                  value={other}
                  onChange={(event) => setOther(event.currentTarget.value)}
                  required
                  size="md"
                />
              )}
              <DateInput
                label="Start date"
                size="md"
                value={start ? new Date(`${start}T00:00:00`) : null}
                onChange={(value) => setStart(value)}
                maxDate={TODAY}
                required
              />
              <Text size="sm" c="dimmed">
                No end date keeps the treatment Active. End it later from the table.
              </Text>
              <Group justify="flex-end" mt="xs">
                <Button variant="default" onClick={() => setModal(undefined)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => save().catch(() => undefined)}
                  disabled={!start || !modality || (modality === 'Other' && !other.trim())}
                >
                  {modal === 'add' ? 'Add treatment' : 'Save changes'}
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </Stack>
    </Paper>
  );
}
